import {
  Component,
  EventEmitter,
  Output,
  Input,
} from '@angular/core';
import { environment } from '../../../../../environments/environment';

interface AddressSuggestion {
  id: string;
  title: string;
  language?: string;
  resultType?: string;
  address?: {
    label?: string;
    countryCode?: string;
    countryName?: string;
    state?: string;
    county?: string;
    city?: string;
    district?: string;
    street?: string;
    houseNumber?: string;
    postalCode?: string;
  };
}

@Component({
  selector: 'app-map-position',
  standalone: false,
  templateUrl: './map-position.component.html',
  styleUrl: './map-position.component.scss',
})
export class MapPositionComponent {
  @Input() address = '';
  @Output() notify = new EventEmitter<{
    lat: number;
    lng: number;
    address: string;
  }>();

  suggestions: AddressSuggestion[] = [];
  private readonly apiKey = environment.hereMapsApiKey;

  constructor() {}
  ngOnInit(): void {
    if(this.address){
      this.searchAddress();
    }
  }

  async onAddressChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    this.address = input.value;

    if (this.address.trim().length < 3) {
      this.suggestions = [];
      return;
    }

    const params = new URLSearchParams({
      q: this.address,
      at: '-12.0464,-77.0428',
      in: 'countryCode:PER',
      limit: '5',
      apiKey: this.apiKey,
    });

    const response = await fetch(
      `https://autosuggest.search.hereapi.com/v1/autosuggest?${params.toString()}`
    );

    const data = await response.json();

    this.suggestions = data.items ?? [];
  }

  async selectSuggestion(suggestion: AddressSuggestion): Promise<void> {
    this.address =
      suggestion.address?.label ??
      suggestion.title;

    this.suggestions = [];

    const url =
      `https://lookup.search.hereapi.com/v1/lookup` +
      `?id=${encodeURIComponent(suggestion.id)}` +
      `&apiKey=${this.apiKey}`;

    const response = await fetch(url);
    const data = await response.json();

    if (!data.position) {
      return;
    }

    this.notify.emit({
      lat: data.position.lat,
      lng: data.position.lng,
      address: data.address?.label ?? this.address,
    });
  }

  getSuggestionDescription(suggestion: AddressSuggestion): string {
    const address = suggestion.address;

    if (!address) {
      return '';
    }

    return [
      address.district,
      address.city,
      address.state,
      address.postalCode,
    ]
      .filter(Boolean)
      .join(', ');
  }

  async searchAddress(): Promise<void> {
    const query = this.address.trim();

    if (!query) {
      return;
    }

    this.suggestions = [];

    const params = new URLSearchParams({
      q: query,
      at: '-12.0464,-77.0428',
      in: 'countryCode:PER',
      limit: '5',
      apiKey: this.apiKey,
    });

    const response = await fetch(
      `https://discover.search.hereapi.com/v1/discover?${params.toString()}`
    );

    const data = await response.json();

    const result = data.items?.[0];

    if (!result?.position) {
      return;
    }

    const event = {
      lat: result.position.lat,
      lng: result.position.lng,
      address: result.address?.label ?? result.title ?? query,
    };

    this.address = event.address;

    this.notify.emit(event);
  }

  clearAddress(): void {
    this.address = '';
    this.suggestions = [];
  }
}